import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Handle, Position, useReactFlow, useUpdateNodeInternals } from 'reactflow';
import { useGetNodeOptionsQuery } from '@frontend/app/hooks';
import { ComboOperator, ComparisonOperator, LogicalOperator } from '@frontend/app/types/automations/condition/Operator';

import { AutomationNodeType, AutomationProvidersEnum, FieldType } from '@frontend/app/types/globalTypes';
import { upperFirst } from 'lodash';
import { format } from 'date-fns';
import { convertToTitleCase } from '@frontend/app/utils/strings';
import { ConditionType } from '@frontend/app/types/automations/condition/Condition';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faLock } from '@fortawesome/free-solid-svg-icons';
import { Plus } from 'lucide-react';
import { Spinner } from '@revfluence/fresh';
import { getConditionOperatorsByFieldType, getNegatedOperator } from '../utils/conditionUtils';
import { EmptyStateButton } from './shared/EmptyStateButton';
import { NodeToolbarV2 } from './shared/NodeToolbarV2';
import { Button } from '@/shadcn/components/ui/button';
import { cn } from '@/shadcn/lib/utils';
import { FlowNodeData } from '../types';
import { NodeIcon } from '../utils/nodeIconMapping';
import { createPlaceholderNode } from '../utils/placeholderUtils';
import DeleteNodeButton from './shared/DeleteNodeButton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shadcn/components/ui/tooltip';

interface ConditionNodeV2Props {
  data: FlowNodeData;
}

export const ConditionNodeV2: React.FC<ConditionNodeV2Props> = ({ data }) => {
  const { nodeId, metadata, isEditing, isSelected, onNodeClick, onDeleteNode, onNodeResize, automationId, templateId, isStaOffer } = data;

  const [showOptionsPopover, setShowOptionsPopover] = useState(false);
  const [showConditionsPopover, setShowConditionsPopover] = useState(false);
  const [clickedBranch, setClickedBranch] = useState<'then' | 'else' | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const conditionsPopoverRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isNodeSelected = data.isSelected;

  // Height measurement for dynamic spacing
  const nodeRef = useRef<HTMLDivElement>(null);

  // Fetch condition options
  const {
    data: { getNodeOptions: { conditionOptions = [] } = { conditionOptions: [] } } = {
      getNodeOptions: { conditionOptions: [] },
    },
    loading: conditionOptionsLoading,
  } = useGetNodeOptionsQuery({
    variables: {
      context: {
        automationId,
        templateId,
        ...(isStaOffer && { providerName: AutomationProvidersEnum.STA_OFFER_EMAILS }),
      },
    },
    skip: !automationId && !templateId,
    fetchPolicy: 'cache-and-network',
  });

  // Determine if we're still loading condition data
  const isLoadingConditions = conditionOptionsLoading;
  // Helper function to get lock status from condition (including nested NOT conditions)
  const getConditionLockStatus = (condition: any): boolean => {
    // Check if this is a NOT wrapper - get lock status from child
    if (condition.type === ConditionType.LOGICAL && condition.op === LogicalOperator.NOT && condition.children && condition.children.length === 1) {
      return getConditionLockStatus(condition.children[0]);
    }

    // Return the locked status of the condition
    return condition.locked || false;
  };

  // Helper function to detect BETWEEN conditions from AND structures
  const detectBetweenCondition = (condition: any): any => {
    // Check if this is an AND condition with exactly 2 children
    if (condition.type === ConditionType.LOGICAL &&
        condition.op === LogicalOperator.AND &&
        condition.children &&
        condition.children.length === 2) {
      const firstChild = condition.children[0];
      const secondChild = condition.children[1];

      // Check if both are COMPARISON nodes with same attribute
      if (firstChild.type === ConditionType.COMPARISON &&
          secondChild.type === ConditionType.COMPARISON &&
          firstChild.attribute === secondChild.attribute) {
        // Check if it's GREATER_THAN_EQUALS and LESS_THAN_EQUALS pattern
        if (firstChild.op === ComparisonOperator.GREATER_THAN_EQUALS && secondChild.op === ComparisonOperator.LESS_THAN_EQUALS) {
          return {
            ...firstChild,
            op: ComboOperator.BETWEEN,
            mappedOperator: ComboOperator.BETWEEN,
            value: [firstChild.value, secondChild.value],
            locked: condition.locked || firstChild.locked || secondChild.locked,
          };
        }
      }
    }

    return condition;
  };

  // Get condition description for node header
  const getConditionDescription = (): string => {
    const singleCondition = metadata?.condition;

    // Check if we have a logical condition (even if empty)
    if (singleCondition?.type === 'LOGICAL') {
      const op = singleCondition.op || 'AND'; // Default to AND
      return op === 'AND' ? 'Check if all are true' : 'Check if any are true';
    }

    // Default for simple/empty conditions
    return 'Check if';
  };

  // Render condition tag with proper formatting
  const renderConditionTag = (condition: any, isNegated: boolean = false): string => {
    // First, try to detect and convert BETWEEN conditions
    const processedCondition = detectBetweenCondition(condition);

    // Check if this is a NOT wrapper around a COMPARISON
    if (processedCondition.type === ConditionType.LOGICAL && processedCondition.op === LogicalOperator.NOT && processedCondition.children && processedCondition.children.length === 1) {
      // This is a NOT wrapper - render the child condition as negated
      return renderConditionTag(processedCondition.children[0], true);
    }

    // Handle other LOGICAL conditions that have children
    if (processedCondition.type === ConditionType.LOGICAL && processedCondition.children && processedCondition.children.length > 0) {
      // Try to detect BETWEEN in the first child, or use the first child as-is
      const firstChildProcessed = detectBetweenCondition(processedCondition.children[0]);
      return renderConditionTag(firstChildProcessed);
    }

    // Handle empty LOGICAL conditions - show as "always true" for backward compatibility
    if (processedCondition.type === ConditionType.LOGICAL && (!processedCondition.children || processedCondition.children.length === 0)) {
      return 'Always true (no conditions set)';
    }

    // Map positive operators to their negative equivalents when negated
    let operator = processedCondition.mappedOperator || processedCondition.op;
    if (isNegated) {
      operator = getNegatedOperator(operator);
    }
    const field = conditionOptions.find((field) => field.id === processedCondition.attribute);
    if (!field) {
      // If condition options are still loading, show loading text instead of raw field ID
      if (isLoadingConditions) {
        return 'Loading condition...';
      }
      return processedCondition.attribute ? `Condition: ${processedCondition.attribute}` : 'No added conditions.';
    }
    const fieldType = field.attributeType;
    const fieldDisplayName = field.displayName ? upperFirst(field.displayName) : field.id;

    // Get group display name dynamically based on what's shown in the dropdown
    // This matches how ConditionFormV2 displays groups using convertToTitleCase
    const getGroupDisplayName = (group: string): string => {
      return convertToTitleCase(group.replace(/_/g, ' '));
    };

    // For locked conditions, don't show the group name (condition type prefix)
    const groupName = field.group ? getGroupDisplayName(field.group) : '';
    const displayName = processedCondition.locked ? fieldDisplayName : (groupName ? `${groupName} ${fieldDisplayName}` : fieldDisplayName);
    const value = processedCondition.value;

    const displayOption = getConditionOperatorsByFieldType(fieldType, operator);
    const inlineText = displayOption?.inlineLabel || '';
    const secondaryInlineText = displayOption?.secondaryInlineLabel || '';

    if (operator === ComboOperator.BETWEEN && (!Array.isArray(value) || value.length !== 2)) {
      throw new Error('Invalid value for between operator');
    }

    switch (fieldType) {
      case FieldType.ANNUAL:
      case FieldType.DATE: {
        const dateFormatString = fieldType === FieldType.DATE ? 'MMM do, yyyy' : 'MMM d';

        switch (operator) {
          case ComparisonOperator.EQUALS:
          case ComboOperator.NOT_EQUALS:
          case ComparisonOperator.GREATER_THAN:
          case ComparisonOperator.GREATER_THAN_EQUALS:
          case ComparisonOperator.LESS_THAN:
          case ComparisonOperator.LESS_THAN_EQUALS: {
            const valueAsDate = format(new Date(value as string), dateFormatString);
            return `${displayName} ${inlineText} ${valueAsDate} ${secondaryInlineText}`;
          }
          case ComboOperator.BETWEEN: {
            if (!Array.isArray(value) || value.length !== 2) {
              throw new Error('Invalid value for between operator');
            }
            const startDate = format(new Date(value[0]), dateFormatString);
            const endDate = format(new Date(value[1]), dateFormatString);
            return `${displayName} is between ${startDate} and ${endDate}`;
          }
          case ComparisonOperator.HAS_VALUE:
          case ComboOperator.IS_NULL:
            return `${displayName} ${inlineText}`;
          case ComparisonOperator.MORE_THAN_AGO:
          case ComboOperator.NOT_MORE_THAN_AGO:
          case ComparisonOperator.IN_THE_LAST:
          case ComboOperator.NOT_IN_THE_LAST:
          case ComboOperator.NOT_IN_THE_NEXT:
          case ComparisonOperator.IN_THE_NEXT:
            return `${displayName} ${inlineText} ${value} ${secondaryInlineText}`;
          default:
            throw new Error('Invalid comparison operator for date attribute');
        }
      }
      case FieldType.ARRAY: {
        switch (operator) {
          case ComparisonOperator.EQUALS:
          case ComboOperator.NOT_EQUALS:
          case ComparisonOperator.CONTAINS:
          case ComboOperator.NOT_CONTAINS: {
            if (!Array.isArray(value)) {
              throw new Error('Invalid value for array attribute');
            }
            const formattedValues = value
              .map((val) => field.choices.find((c) => c.value === val)?.displayName || val)
              .join(', ');
            return `${displayName} ${inlineText} ${formattedValues} ${secondaryInlineText}`;
          }
          case ComboOperator.IS_NULL:
          case ComparisonOperator.HAS_VALUE:
            return `${displayName} ${inlineText}`;
          default:
            throw new Error('Invalid comparison operator for array attribute');
        }
      }
      case FieldType.BOOLEAN: {
        const boolValue = value ? 'Yes' : 'No';
        switch (operator) {
          case ComparisonOperator.EQUALS:
          case ComboOperator.NOT_EQUALS:
            // Use the proper inline text for "is" vs "is not"
            return `${displayName} ${inlineText} ${boolValue}`;
          case ComparisonOperator.HAS_VALUE:
          case ComboOperator.IS_NULL:
            return `${displayName} ${inlineText}`;
          default:
            console.warn('Unknown boolean operator, using fallback:', operator);
            return `${displayName}: ${boolValue}`;
        }
      }
      case FieldType.NUMBER:
      case FieldType.PERCENTAGE:
        switch (operator) {
          case ComparisonOperator.EQUALS:
          case ComparisonOperator.GREATER_THAN:
          case ComparisonOperator.GREATER_THAN_EQUALS:
          case ComparisonOperator.LESS_THAN:
          case ComparisonOperator.LESS_THAN_EQUALS:
          case ComboOperator.NOT_EQUALS:
            return `${displayName} ${inlineText} ${value}`;
          case ComboOperator.BETWEEN: {
            if (!Array.isArray(value) || value.length !== 2) {
              throw new Error('Invalid value for between operator');
            }
            return `${displayName} is between ${value[0]} and ${value[1]}`;
          }
          case ComboOperator.IS_NULL:
          case ComparisonOperator.HAS_VALUE:
            return `${displayName} ${inlineText}`;
          default:
            throw new Error('Invalid comparison operator for number attribute');
        }
      case FieldType.TEXT:
      case FieldType.EMAIL:
        switch (operator) {
          case ComparisonOperator.EQUALS:
          case ComparisonOperator.CONTAINS:
          case ComboOperator.NOT_CONTAINS:
          case ComboOperator.NOT_EQUALS:
            return `${displayName} ${inlineText} ${value} ${secondaryInlineText}`;
          case ComboOperator.IS_NULL:
          case ComparisonOperator.HAS_VALUE:
            return `${displayName} ${inlineText}`;
          default:
            throw new Error('Invalid comparison operator for text attribute');
        }
      default:
        console.warn('Unknown field type, using fallback display:', fieldType);
        // Fallback for unknown field types
        if (operator === ComboOperator.IS_NULL) {
          return `${displayName} is null`;
        } else if (operator === ComparisonOperator.HAS_VALUE) {
          return `${displayName} has value`;
        } else if (typeof value === 'boolean') {
          return `${displayName} is ${value ? 'True' : 'False'}`;
        } else if (value !== null && value !== undefined) {
          return `${displayName} ${inlineText || 'equals'} ${value}`;
        } else {
          return `${displayName} ${inlineText || 'condition'}`;
        }
    }
  };

  const handleNodeClick = () => {
    onNodeClick?.(nodeId);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowOptionsPopover(false);
    onDeleteNode?.(nodeId);
  };

  const handleOptionsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowOptionsPopover(!showOptionsPopover);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Open properties panel by selecting this node
    onNodeClick?.(nodeId);
  };

  const handleAddConditionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Open properties panel to add condition
    onNodeClick?.(nodeId);
  };

  const handleShowPopover = () => {
    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    if (conditionsPopoverRef.current) {
      const rect = conditionsPopoverRef.current.getBoundingClientRect();
      setPopoverPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
      });
      setShowConditionsPopover(true);
    }
  };

  const handleHidePopover = () => {
    // Add a small delay before hiding to allow mouse movement to popover
    hideTimeoutRef.current = setTimeout(() => {
      setShowConditionsPopover(false);
      setPopoverPosition(null);
    }, 150);
  };

  const handlePopoverMouseEnter = () => {
    // Clear any pending hide timeout when entering popover
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handlePopoverMouseLeave = () => {
    // Hide immediately when leaving popover
    setShowConditionsPopover(false);
    setPopoverPosition(null);
  };

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setShowOptionsPopover(false);
      }
    };

    if (showOptionsPopover) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showOptionsPopover]);

  // Only use metadata.condition format (no more children support)
  const singleCondition = metadata?.condition;

  // For content display, only show conditions if there are actual condition rules
  // Empty LOGICAL conditions should show "No conditions added" message
  const hasActualConditions =
  singleCondition &&
  ((singleCondition.attribute && singleCondition.op) || // Regular single condition with actual data
    (singleCondition.type === ConditionType.LOGICAL && singleCondition.op === LogicalOperator.NOT && singleCondition.children?.length > 0) || // Single NOT wrapper with child
    (singleCondition.type === ConditionType.LOGICAL && singleCondition.children?.length > 0)); // LOGICAL single condition with actual children

  const hasConditions = hasActualConditions;

  // Function to get all conditions from metadata.condition only
  const getAllConditions = () => {
    let allConditions = [];

    if (singleCondition && singleCondition.type === ConditionType.LOGICAL && singleCondition.op === LogicalOperator.NOT) {
      // Single NOT wrapper - return the NOT wrapper itself to preserve negation context
      allConditions = [singleCondition];
    } else if (singleCondition && singleCondition.type === ConditionType.LOGICAL && singleCondition.children) {
      // LOGICAL condition with multiple children - include both locked and unlocked conditions
      // Also include NOT wrappers as they represent actual conditions
      allConditions = singleCondition.children.filter(
        (c: any) => c && (c.attribute || c.type === ConditionType.LOGICAL),
      );
    } else if (singleCondition && singleCondition.attribute) {
      // Single direct condition
      allConditions = [singleCondition];
    }

    return allConditions;
  };

  // Function to render a single condition as text
  const renderConditionText = (condition: any): string => {
    try {
      return renderConditionTag(condition);
    } catch (error) {
      return 'Condition';
    }
  };

  // Position constants for new layout
  const X_LEFT = 15;
  const X_CENTER = 50;
  const X_RIGHT = 85;

  // Layout logic using ReactFlow edge detection:
  const reactFlow = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();

  // Get current edges from ReactFlow
  const edges = reactFlow.getEdges();

  // Check for child connections using ReactFlow edges (include placeholders, exclude End node edges)
  const hasThenChild = edges.some(
    (edge) =>
      edge.source === nodeId &&
      edge.sourceHandle === 'true' &&
      !edge.target.includes('-end') && // Exclude End node edges
      edge.target !== `${nodeId}-then-end` &&
      edge.target !== `${nodeId}-else-end` &&
      edge.target !== `${nodeId}-center-end`,
  );
  // Check for Else branch presence - includes both actual children AND placeholder nodes
  // Specifically check for any edge from 'false' handle that's not an end node
  const hasElseBranch = edges.some(
    (edge) =>
      edge.source === nodeId &&
      edge.sourceHandle === 'false' &&
      (edge.target.includes('-placeholder') || // Include placeholder nodes
        (!edge.target.includes('-end') && // Or any non-end node
          edge.target !== `${nodeId}-then-end` &&
          edge.target !== `${nodeId}-else-end` &&
          edge.target !== `${nodeId}-center-end`)),
  );

  // For backward compatibility, keep hasElseChild for other parts of the code
  const hasElseChild = hasElseBranch;

  // Reset clicked branch state when children are created
  useEffect(() => {
    if (hasThenChild || hasElseBranch) {
      setClickedBranch(null);
    }
  }, [hasThenChild, hasElseBranch]);

  // Updated layout logic based on requirements:
  // 1. Initial state (no Else branch): Then center (50%), Else right (85%)
  // 2. When Else branch exists (including placeholder): Then left (15%), Else right (85%)
  // 3. Then position depends on Else branch presence, not Then's own children
  const thenPosition = hasElseBranch ? X_LEFT : X_CENTER; // LEFT when Else branch exists, CENTER when no Else
  const elsePosition = X_RIGHT; // Always at RIGHT (85%)

  // Force ReactFlow to update handle positions when they change
  useEffect(() => {
    // Small delay to ensure positions are updated after render
    const timer = setTimeout(() => {
      updateNodeInternals(nodeId);
    }, 10);
    return () => clearTimeout(timer);
  }, [thenPosition, elsePosition, updateNodeInternals, nodeId]);

  // Height measurement effect for dynamic spacing
  useLayoutEffect(() => {
    if (nodeRef.current && onNodeResize) {
      const measureHeight = () => {
        const height = nodeRef.current?.offsetHeight;
        if (height) {
          onNodeResize(nodeId, height);
        }
      };

      // Initial measurement
      measureHeight();

      // Create ResizeObserver for dynamic height changes
      const resizeObserver = new ResizeObserver(measureHeight);
      resizeObserver.observe(nodeRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [nodeId, onNodeResize, metadata?.condition, metadata?.children]); // Re-measure when conditions change

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  return (
    <TooltipProvider>
      <div ref={nodeRef} className="relative">
        {/* Input handle */}
        <Handle type="target" position={Position.Top} isConnectable={false} className="w-0 h-0 opacity-0" />

        {/* Main node container */}
        <div className="flex flex-col items-start justify-center relative w-[342px] transition-all duration-200">
          {/* Node with border */}
          <div
            className={cn(
              'w-full bg-white border border-gray-200 rounded-2xl transition-all duration-200 pb-4',
              isSelected && 'border-grey-6 border-[1px] shadow-md shadow-black/10',
            )}
          >
            {/* Header Card */}
            <div className="w-full bg-white cursor-pointer rounded-t-2xl" onClick={handleNodeClick}>
              <div className="flex h-12 items-center gap-1 p-3 relative w-full">
                <div className="flex justify-between flex-1 self-stretch grow items-center relative">
                  <div className="inline-flex items-center gap-2 relative flex-[0_0_auto]">
                    <NodeIcon node={{ type: AutomationNodeType.CONDITION }} size="md" />
                    <div className="relative w-fit font-medium text-gray-900 text-sm tracking-[0] leading-[22px] whitespace-nowrap">
                      {getConditionDescription()}
                    </div>
                  </div>
                  <div className="inline-flex gap-2 flex-[0_0_auto] items-center relative">
                    <div ref={popoverRef}>
                      <DeleteNodeButton
                        handleDelete={handleDelete}
                        showOptionsPopover={showOptionsPopover}
                        setShowOptionsPopover={setShowOptionsPopover}
                        handleOptionsClick={handleOptionsClick}
                        isEditing={isEditing}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Card */}
            <div className="w-full bg-white rounded-b-2xl overflow-hidden">
              <div className="flex flex-col items-start gap-3 px-3 pb-10 relative w-full bg-white">
                {isLoadingConditions ? (
                  // Show loading spinner when condition options are being fetched
                  <div className="flex flex-row items-center justify-center gap-2 px-6 py-3 w-full">
                    <div className="text-sm text-grey-5">Loading conditions...</div>
                    <Spinner size="small" />
                  </div>
                ) : hasConditions ? (
                  <div className="flex flex-col items-start gap-2 py-2 px-3 relative bg-grey-1 rounded-xl w-full">
                    {/* Show appropriate icon in top-right based on locked status */}
                    {isEditing && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleEditClick}
                          className="absolute top-3 right-3 w-6 h-6 p-0 flex items-center justify-center hover:bg-grey-1 z-10"
                        >
                          <FontAwesomeIcon icon={faPen} className="h-3 w-3 text-grey-5" />
                        </Button>
                      </>
                    )}

                    {(() => {
                      const allConditions = getAllConditions();
                      const conditionsToShow = Math.min(allConditions.length, 2);
                      const remainingCount = Math.max(0, allConditions.length - 2);

                      return (
                        <div className="flex flex-col gap-2 w-full pr-8">
                          {/* Display first 2 conditions as individual tags - show full text without truncation */}
                          {allConditions.slice(0, conditionsToShow).map((condition, index) => (
                            <div
                              key={index}
                              className={cn(
                                'p-2 rounded-lg text-xs font-medium flex items-center gap-2 bg-grey-2 text-grey-6',
                                'break-words max-w-full', // Allow text to wrap and show full content
                              )}
                            >
                              <span className="break-words">{renderConditionText(condition)}</span>
                              {getConditionLockStatus(condition) && <FontAwesomeIcon icon={faLock} className="h-3 w-3 text-grey-4 flex-shrink-0" />}
                            </div>
                          ))}

                          {/* Display remaining count if more than 2 conditions */}
                          {remainingCount > 0 && (
                            <div
                              className="relative"
                              ref={conditionsPopoverRef}
                              onMouseEnter={handleShowPopover}
                              onMouseLeave={handleHidePopover}
                            >
                              <div className="px-3 py-2 bg-grey-2 rounded-lg text-xs font-medium text-grey-5 w-fit hover:bg-grey-3 transition-colors cursor-pointer">
                                +{remainingCount} more
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  // Custom empty state for condition nodes
                  <EmptyStateButton nodeType={AutomationNodeType.CONDITION} onClick={handleAddConditionClick} />
                )}
              </div>
            </div>
          </div>

          {/* Plus buttons positioned based on new layout */}
          {isEditing && (
            <div className="relative w-full -mt-4 z-10">
              {/* Then button container - positioned at thenPosition */}
              <div className="absolute" style={{ left: `${thenPosition}%`, transform: 'translateX(-50%)' }}>
                <div className="relative flex items-center justify-center">
                  {/* Background mask to hide connecting line behind button */}
                  <div className="absolute w-8 h-8 bg-white rounded-full z-0" />

                  {/* Semicircular arc border - top half */}
                  <svg
                    width="37"
                    height="37"
                    viewBox="0 0 36 36"
                    className="absolute -top-0.5 z-10"
                  >
                    <path
                      d="M 18 3 A 15 15 0 0 1 33 18"
                      stroke={isNodeSelected ? '#000000' : '#e5e7eb'}
                      strokeWidth={1}
                      fill="none"
                    />
                    <path
                      d="M 3 18 A 15 15 0 0 1 18 3"
                      stroke={isNodeSelected ? '#000000' : '#e5e7eb'}
                      strokeWidth={1}
                      fill="none"
                    />
                  </svg>

                  {/* Then button */}
                  <Button
                    size="sm"
                    className="relative flex w-6 h-6 items-center justify-center p-0 bg-green-600 rounded-full hover:bg-green-700 shadow-md ring-0 m-[6px] z-20"
                    data-plus-button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (hasThenChild) {
                        // Then has child - show NodeToolbar for inline insertion
                        data.onRequestToolbarOpen?.(nodeId);
                        data.onShowDashedPlaceholder?.(nodeId, false, hasElseChild);
                      } else {
                        // Then is empty - show NodeToolbar for adding between Then and End
                        setClickedBranch('then');
                        data.onRequestToolbarOpen?.(nodeId);
                        data.onShowDashedPlaceholder?.(nodeId, false, hasElseChild, 'then');
                      }
                    }}
                  >
                    <Plus className="w-3 h-3 text-white" strokeWidth={2.5} />
                  </Button>
                </div>
              </div>

              {/* Else button container - always positioned at elsePosition (X_RIGHT) */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="absolute" style={{ left: `${elsePosition}%`, transform: 'translateX(-50%)' }}>
                    <div className="relative flex items-center justify-center">
                      {/* Background mask to hide connecting line behind button */}
                      <div className="absolute w-8 h-8 bg-white rounded-full z-0" />

                      {/* Semicircular arc border - top half */}
                      <svg
                        width="37"
                        height="37"
                        viewBox="0 0 36 36"
                        className="absolute -top-0.5 z-10"
                      >
                        <path
                          d="M 18 3 A 15 15 0 0 1 33 18"
                          stroke={isNodeSelected ? '#000000' : '#e5e7eb'}
                          strokeWidth={1}
                          fill="none"
                        />
                        <path
                          d="M 3 18 A 15 15 0 0 1 18 3"
                          stroke={isNodeSelected ? '#000000' : '#e5e7eb'}
                          strokeWidth={1}
                          fill="none"
                        />
                      </svg>

                      {/* Else button */}
                      <Button
                        size="sm"
                        className="relative flex w-6 h-6 items-center justify-center p-0 bg-red-600 rounded-full hover:bg-red-700 shadow-md ring-0 m-[6px] z-20"
                        data-plus-button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (hasElseChild) {
                            // Else has child - show NodeToolbar for inline insertion
                            data.onRequestToolbarOpen?.(nodeId);
                            data.onShowDashedPlaceholder?.(nodeId, true, hasThenChild);
                          } else {
                            // Else is empty - directly create placeholder (no End for Else)
                            data.onAddChild?.(nodeId, createPlaceholderNode('else'), true, true);
                          }
                        }}
                      >
                        <Plus className="w-3 h-3 text-white" strokeWidth={2.5} />
                      </Button>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent
                  side="right"
                  align="start"
                  sideOffset={10}
                  className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium max-w-[184px]"
                >
                  <p>Creators who don't meet the conditions above will follow this path.</p>
                </TooltipContent>
              </Tooltip>
            </div>
          )}

          {/* Labels positioned above plus icons */}
          {isEditing && (
            <div className="absolute w-full z-20 pointer-events-none" style={{ bottom: '20px' }}>
              <div className="relative w-full">
                {/* Then label - positioned at thenPosition */}
                <div
                  className="absolute text-xs text-grey-5 font-medium"
                  style={{ left: `${thenPosition}%`, transform: 'translateX(-50%)' }}
                >
                  Then
                </div>
                {/* Else label - always positioned at elsePosition (X_RIGHT) */}
                <div
                  className="absolute text-xs text-grey-5 font-medium"
                  style={{ left: `${elsePosition}%`, transform: 'translateX(-50%)' }}
                >
                  Else
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Output handles positioned based on new layout */}
        <div key={`handles-${thenPosition}-${elsePosition}`}>
          {/* Then handle - positioned based on Else presence */}
          <Handle
            type="source"
            position={Position.Bottom}
            id="true"
            isConnectable={false}
            className="w-0 h-0 opacity-0"
            style={{ left: `${thenPosition}%`, transform: 'translateX(-50%)' }}
          />
          {/* Else handle - positioned using direct percentage */}
          <Handle
            type="source"
            position={Position.Bottom}
            id="false"
            isConnectable={false}
            className="w-0 h-0 opacity-0"
            style={{ left: `${elsePosition}%`, transform: 'translateX(-50%)' }}
          />

          {/* Center handle for End node connection when Then is empty and clicked */}
          {!hasThenChild && clickedBranch === 'then' && (
            <Handle
              type="source"
              position={Position.Bottom}
              id="center-then"
              isConnectable={false}
              className="w-0 h-0 opacity-0"
              style={{ left: `${thenPosition}%`, transform: 'translateX(-50%)' }}
            />
          )}

          {/* Center handle for End node connection when no children exist (default state) */}
          {!hasThenChild && !hasElseChild && !clickedBranch && (
            <Handle
              type="source"
              position={Position.Bottom}
              id="center"
              isConnectable={false}
              className="w-0 h-0 opacity-0"
              style={{ left: `${thenPosition}%`, transform: 'translateX(-50%)' }}
            />
          )}
        </div>

        {/* Node Toolbar */}
        {data.isToolbarVisible && (
          <NodeToolbarV2
            nodeId={nodeId}
            isVisible
            onAddNode={data.onAddChild}
            onClose={() => {
              data.onCloseToolbar?.();
              data.onShowDashedPlaceholder?.(null);
              // Reset clicked branch state when toolbar closes
              setClickedBranch(null);
            }}
            position={Position.Right}
            offset={15}
            isElseBranch={data.toolbarBranch?.isElseBranch}
          />
        )}

        {/* Portal-based popover for additional conditions */}
        {showConditionsPopover && popoverPosition && (() => {
          const allConditionsForPopover = getAllConditions();
          const remainingCountForPopover = Math.max(0, allConditionsForPopover.length - 2);
          if (remainingCountForPopover === 0) return null;

          return createPortal(
            <div
              className="fixed bg-white border border-gray-200 rounded-xl shadow-lg p-3 min-w-[200px] max-w-sm user-select-text pointer-events-auto"
              style={{
                top: popoverPosition.top,
                left: popoverPosition.left,
                zIndex: 999999,
              }}
              onMouseEnter={handlePopoverMouseEnter}
              onMouseLeave={handlePopoverMouseLeave}
            >
              <div className="flex flex-col gap-2">
                <div
                  className="flex flex-col gap-1.5 max-h-48 overflow-y-auto"
                  style={{
                    // Prevent scroll events from bubbling to ReactFlow
                    WebkitOverflowScrolling: 'touch',
                  }}
                  onWheel={(e) => {
                    // Stop propagation to prevent ReactFlow zoom
                    e.stopPropagation();
                  }}
                >
                  {allConditionsForPopover.slice(2).map((condition, index) => (
                    <div
                      key={index + 2}
                      className={cn(
                        'px-2 py-1.5 rounded text-xs font-medium flex items-center gap-1.5 bg-grey-2 text-grey-6',
                      )}
                    >
                      <span className="break-words">{renderConditionText(condition)}</span>
                      {getConditionLockStatus(condition) && (
                        <FontAwesomeIcon
                          icon={faLock}
                          className="h-2.5 w-2.5 text-grey-4 flex-shrink-0"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>,
            document.body
          );
        })()}
      </div>
    </TooltipProvider>
  );
};
